import React, { useEffect, useRef } from "react";
import Card from "../../components/common/Card";
import HeadingBox from "../../components/common/HeadingBox";
import { Box, Text, Flex } from "@radix-ui/themes";
import aboutImage1 from "../../assets/home/services/eye-care.jpg";
import aboutImage2 from "../../assets/home/services/hair-care.jpg";
import founderImage from "../../assets/home/alondra.png";
import styles from "./styles.module.scss";
import {
  animateHeroCard,
  animatePoeticSection,
  animateFounderSection,
  animateAboutMissionCard,
  animateValuesSection,
  refreshScrollTrigger,
} from "../../utils/animations";

const About = () => {
  // Refs for animations
  const heroCardRef = useRef(null);
  const poeticSectionRef = useRef(null);
  const founderSectionRef = useRef(null);
  const missionCardRef = useRef(null);
  const valuesSectionRef = useRef(null);

  // Animation for Hero Card
  useEffect(() => {
    return animateHeroCard(heroCardRef);
  }, []);

  // Animation for Poetic Section
  useEffect(() => {
    return animatePoeticSection(poeticSectionRef);
  }, []);

  // Animation for Founder Section
  useEffect(() => {
    return animateFounderSection(founderSectionRef);
  }, []);

  // Animation for Mission Card
  useEffect(() => {
    return animateAboutMissionCard(missionCardRef);
  }, []);

  // Animation for Values Section
  useEffect(() => {
    return animateValuesSection(valuesSectionRef);
  }, []);

  // Refresh ScrollTrigger after all animations are set up
  useEffect(() => {
    return refreshScrollTrigger();
  }, []);

  return (
    <div style={{ background: "var(--color-secondary)", minHeight: "100vh" }}>
      {/* Hero Card Section - First Section (Beige/Secondary) */}
      <div ref={heroCardRef}>
        <Card
        image={aboutImage1}
        imagePosition="right"
        subtitle="Our Clinic"
        title="Renova Aesthetic Clinic"
        description="Located in the heart of Zahle, Renova Aesthetic Clinic stands as a sanctuary of renewal and transformation. We offer a comprehensive range of advanced aesthetic services, each carefully curated to address your unique needs. From facial rejuvenation to body sculpting, hair restoration to laser treatments—we provide solutions that honor both your outer beauty and inner confidence. Step into Renova, where your journey to rediscovering your natural glow begins."
        backgroundColor="var(--color-secondary)"
        cardBackgroundColor="var(--color-secondary)"
        textColor="var(--color-primary)"
        sectionPadding="0"
        cardPadding="4rem"
        className={styles.heroSection}
        cardClassName={styles.heroCard}
        button={{
          label: "Explore Our Services",
          onClick: () => {
            window.location.href = "/services";
          },
          variant: "normal",
          size: "3",
          backgroundColor: "var(--color-primary)",
          textColor: "var(--color-secondary)",
        }}
        />
      </div>

      {/* Poetic Section */}
      <Box ref={poeticSectionRef} className={styles.poeticSection}>
        <Text className={styles.poeticText}>
          "Regenerate your skin's story, restore its natural glow.
          <br />
          Take care of what reflects your inner beauty,
          <br />
          and watch it flourish with time."
        </Text>
      </Box>

      {/* Founder Story - Centered Layout (Primary for contrast) */}
      <Box ref={founderSectionRef} className={styles.founderSectionPrimary}>
        <Flex
          direction="column"
          align="center"
          className={styles.founderContent}
        >
          <Box className={styles.founderImageWrapper}>
            <img
              src={founderImage}
              alt="Dr. Alondra Azar"
              className={styles.founderImage}
            />
          </Box>
          <Box className={styles.founderText}>
            <Text className={styles.founderSubtitle}>
              The Visionary Founder
            </Text>
            <Text className={styles.founderName}>Dr. Alondra Azar</Text>
            <Text className={styles.founderCredential}>
              PhD in Pharmacy • Skincare Specialist
            </Text>
            <Text className={styles.founderDescription}>
              A PhD in Pharmacy with an unwavering passion for skincare, Dr.
              Alondra Azar founded Renova with a simple yet profound mission: to
              provide natural solutions that help girls overcome their
              insecurities and reveal the natural beauty that lies within. Her
              journey is one of combining medical expertise with heartfelt
              dedication—creating treatments that nurture not just the skin, but
              the soul.
            </Text>
            <Text className={styles.founderQuote}>
              "My goal was to give solutions to all girls' insecurities
              naturally and help them reveal their natural beauty within—through
              services that honor both medical excellence and compassionate
              care."
            </Text>
          </Box>
        </Flex>
      </Box>

      {/* Mission Card - Left Image */}
      <div ref={missionCardRef}>
        <Card
        image={aboutImage2}
        imagePosition="left"
        subtitle="Our Philosophy"
        title="Healing from Within, Shining from Without"
        description="Dr. Alondra combines her extensive medical knowledge with genuine passion to offer services that care for both physical and mental wellbeing. At Renova, we believe that true beauty treatment extends beyond the surface—it's a journey of self-discovery, confidence-building, and long-term wellness. Every solution we offer is designed to work in harmony with your body's natural processes, helping you feel beautiful in your own skin, naturally."
        backgroundColor="var(--color-secondary)"
        cardBackgroundColor="var(--color-secondary)"
        textColor="var(--color-primary)"
        sectionPadding="4rem 2rem"
        cardPadding="3rem"
        className={styles.customSectionLeft}
        cardClassName={styles.customCardLeft}
        button={{
          label: "Meet Our Team",
          onClick: () => {
            window.location.href = "/team";
          },
          variant: "normal",
          size: "3",
          backgroundColor: "var(--color-primary)",
          textColor: "var(--color-secondary)",
        }}
        />
      </div>

      {/* Values Section (Primary for contrast) */}
      <Box ref={valuesSectionRef} className={styles.valuesSectionPrimary}>
        <HeadingBox
          title="Our Promise to You"
          description="Where science meets soul, and beauty meets authenticity"
        />
        <Box className={styles.valuesGrid}>
          <Box className={styles.valueCard}>
            <Text className={styles.valueIcon}>✨</Text>
            <Text className={styles.valueTitle}>Natural Solutions</Text>
            <Text className={styles.valueText}>
              We honor your body's natural processes, working with nature to
              reveal your authentic beauty.
            </Text>
          </Box>
          <Box className={styles.valueCard}>
            <Text className={styles.valueIcon}>💊</Text>
            <Text className={styles.valueTitle}>Medical Excellence</Text>
            <Text className={styles.valueText}>
              Rooted in pharmaceutical expertise, every treatment is backed by
              scientific knowledge and precision.
            </Text>
          </Box>
          <Box className={styles.valueCard}>
            <Text className={styles.valueIcon}>💝</Text>
            <Text className={styles.valueTitle}>Holistic Care</Text>
            <Text className={styles.valueText}>
              We nurture both your physical wellness and mental confidence,
              creating lasting transformations.
            </Text>
          </Box>
          <Box className={styles.valueCard}>
            <Text className={styles.valueIcon}>🌟</Text>
            <Text className={styles.valueTitle}>Long-term Results</Text>
            <Text className={styles.valueText}>
              Our approach focuses on sustainable beauty that grows stronger
              with time, not quick fixes.
            </Text>
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export default About;
